import { isDate } from "./time";
import { readPathValue } from "./string";
import { isTimestamp } from "../typings/generics";
import {
  getDateTimeFieldTimestamp,
  isDateTimeFieldStoredValue,
} from "./date-time-field";
import { traverseObjectPath } from "./object.traverse";
import { hasMatches, isArrayObjectMatch, pathRegex, tryJSON } from "./object.utilities";

export { setObj } from "./object.set";
export { traverseObjectPath } from "./object.traverse";

// HACK: this partial<unknown> is needed for firestore type bug
export type FlatType<T = unknown> = Record<string, Partial<unknown> | Primitive | T>;

function isPrimitiveValue(val: unknown): val is Primitive {
  return (
    val === null || typeof val === "string" || typeof val === "number" || typeof val === "boolean"
  );
}

export type Primitive = string | number | boolean | undefined | null;

export function somePath(
  obj: object,
  predicate: (path: string, value: unknown) => boolean,
  options?: {
    preserveArray?: boolean;
    preserveDates?: boolean;
    preservePaths?: readonly string[];
  },
): boolean {
  if (!isObject(obj)) {
    throw new Error("obj is not an object");
  }

  const paths = Object.entries(obj)
    .map(([path, value]) => ({ path, value }))
    .reverse();

  while (paths.length) {
    const current = paths.pop();
    if (!current) {
      continue;
    }

    const { path, value } = current;
    
    const preservePath = options?.preservePaths?.includes(path);
    
    const preserveArray =
      options?.preserveArray &&
      Array.isArray(value) &&
      value.every(isPrimitiveValue);
    
      const preserveDate =
      options?.preserveDates &&
      isObject(value) &&
      !Array.isArray(value) &&
      (isTimestamp(value) || isDate(value) || isDateTimeFieldStoredValue(value));

    if (preservePath || preserveArray || preserveDate) {
      if (predicate(path, value)) {
        return true;
      }
      continue;
    }

    if (isObject(value)) {
      const nestedPaths = Object.entries(value).map(([key, nestedValue]) => ({
        path: `${path}.${key}`,
        value: nestedValue,
      }));

      if (nestedPaths.length) {
        paths.push(...nestedPaths.reverse());
      } else if (predicate(path, value)) {
        return true;
      }
    } else if (predicate(path, value)) {
      return true;
    }  }

  return false;
}

export function flattenObject<T = string, O = Record<string, unknown>>(
  obj: O,
  depth?: number | undefined,
  options?: {
    preserveArray?: boolean;
    preserveDates?: boolean;
    preservePaths?: string[];
  },
  parent?: string,
): FlatType<T> {
  if (depth === 1) {
    return obj as FlatType<T>;
  }
  const returnObj: FlatType<T> = {};

  if (!isObject(obj)) {
    throw new Error("obj is not an object");
  }

  Object.entries(obj).forEach(([key, value]) => {
    const fullPath = parent ? parent + "." + key : key;
    if (options?.preservePaths && options?.preservePaths?.includes(fullPath)) {
      returnObj[key] = value as Partial<unknown>;
    } else if (options?.preserveArray && Array.isArray(value) && value.every(isPrimitiveValue)) {
      returnObj[key] = value;
      // add array entries index too (flatten will override / merge the above and the below)
      Object.entries(
        flattenObject(value, depth ? depth - 1 : undefined, options, fullPath),
      ).forEach(([flatKey, flatValue]) => {
        returnObj[key + "." + flatKey] = flatValue;
      });
    } else if (
      // preserve date objects
      options?.preserveDates &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (isTimestamp(value) || isDate(value) || isDateTimeFieldStoredValue(value))
    ) {
      returnObj[key] = value;
    } else if (typeof value === "object" && value !== null) {
      Object.entries(
        flattenObject(
          value as Record<string, unknown>,
          depth ? depth - 1 : undefined,
          options,
          fullPath,
        ),
      ).forEach(([flatKey, flatValue]) => {
        returnObj[key + "." + flatKey] = flatValue;
      });
    } else if (isPrimitiveValue(value)) {
      returnObj[key] = value;
    }
  });
  return returnObj;
}

// TODO: add unit tests
export const unFlattenObject = <T>(obj: Record<string, unknown>, delimiter = ".") => {
  const result: Record<string, unknown> = {};
  for (const i in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, i)) {
      const keys = i.split(delimiter);
      // oxlint-disable-next-line typescript/no-explicit-any
      keys.reduce(function (r: any, e, j) {
        return (
          r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 == j ? obj[i] : {}) : [])
        );
      }, result);
    }
  }
  return result as T;
};

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function safeReadObject(
  obj: unknown,
  path: string,
): Record<string, unknown> | undefined {
  const value = readPathValue(obj, path);
  return isObject(value) ? value : undefined;
}

export function hasPath(path: string, obj: object): boolean {
  const fragments = (path || "").split(pathRegex);
  let _head = obj;

  while (fragments) {
    if (!_head) {
      return false;
    }

    const fragment = fragments.shift();
    if (isArrayObjectMatch(fragment)) {
      const findObj = tryJSON(fragment);

      if (!Array.isArray(_head) || !findObj) {
        return false;
      }
      _head = _head.find(hasMatches(findObj));
    } else {
      _head = _head[fragment as keyof typeof obj];
    }
  }

  return true;
}

export function removePrivateProps(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.getOwnPropertyNames(obj).reduce<Record<string, unknown>>((acc, name) => {
    if (!/^_(.)/g.test(name) || name.startsWith("__")) {
      acc[name] = obj[name];
    }
    return acc;
  }, {});
}

export function inverseObject(
  fv: Record<string, unknown>,
  options: { flat: true },
): Record<string, string>;
export function inverseObject(
  fv: Record<string, unknown>,
  options?: { flat: boolean },
): Record<string, string | unknown> {
  if (options?.flat) {
    return Object.fromEntries(Object.entries(flattenObject(fv)).map(([key, val]) => [val, key]));
  }
  return unFlattenObject(
    Object.fromEntries(Object.entries(flattenObject(fv)).map(([key, val]) => [val, key])),
  );
}

export function pivotRecordArray<T = string>(obj: Record<string, T[]>): T[] {
  return [
    ...(Object.keys(obj)
      .reduce<Set<T>>((_set, key) => {
        obj[key].forEach((_uid) => _set.add(_uid));
        return _set;
      }, new Set<T>())
      .values() as unknown as T[]),
  ];
}

export function getDateNumber(data: unknown, path: string): number | undefined | null {
  const targetData = traverseObjectPath<unknown>(path, data, true);

  if (targetData === null) {
    return null;
  }

  if (targetData === undefined || targetData === "") {
    return undefined;
  }

  if (
    typeof targetData === "number" ||
    typeof targetData === "string" ||
    isDateTimeFieldStoredValue(targetData)
  ) {
    return getDateTimeFieldTimestamp(targetData);
  }

  if (isTimestamp(targetData)) {
    return targetData.toDate().getTime();
  }
  return undefined;
}
